import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  FileSearch, Sparkles, TrendingUp, Brain, Briefcase, 
  Trophy, Users, Target, Zap, ArrowRight, CheckCircle,
  User, LogOut, Menu, X, ChevronRight, Award, BarChart3, Share2
} from 'lucide-react';
import EngagementModal from '../components/EngagementModal';
import GetStartedModal from '../components/GetStartedModal';
import Footer from '../components/Footer';
import { ProfileNudgeBanner } from '../components/ProfileNudgeBanner';
import { AwardsBanner } from '../components/AwardsBanner';
import SEO from '../components/SEO';
import { seoConfig, softwareApplicationSchema } from '../config/seoConfig';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';

const HomePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, loading, logout } = useAuth();
  const { toast } = useToast();
  const [showEngagementModal, setShowEngagementModal] = useState(false);
  const [showGetStartedModal, setShowGetStartedModal] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Handle scroll for header
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Show engagement modal after 10 seconds (only once per session)
  useEffect(() => {
    const hasSeenModal = localStorage.getItem('recruiteriq_engagement_shown');
    
    if (!hasSeenModal) {
      const timer = setTimeout(() => {
        setShowEngagementModal(true);
        localStorage.setItem('recruiteriq_engagement_shown', 'true');
      }, 10000);

      return () => clearTimeout(timer);
    }
  }, []);

  const features = [
    {
      icon: TrendingUp,
      title: 'Market Intelligence',
      description: 'AI-powered market insights, salary benchmarks, and competitive analysis',
      gradient: 'from-blue-500 to-cyan-500',
      hoverGradient: 'group-hover:from-blue-600 group-hover:to-cyan-600',
      route: '/market-intelligence',
      features: [
        'Instant salary benchmarks',
        'Competitive landscape analysis',
        'Talent availability insights',
        'Sourcing strategies & email templates'
      ],
      badge: 'Most Popular',
      badgeColor: 'bg-blue-500'
    },
    {
      icon: Zap,
      title: 'Advanced AI Sourcing',
      description: 'Find 25+ candidates instantly with o1 model → Select → Analyze with SmartMatch',
      gradient: 'from-violet-500 to-purple-500',
      hoverGradient: 'group-hover:from-violet-600 group-hover:to-purple-600',
      route: '/advanced-sourcing',
      features: [
        'Chat with AI to find 25+ candidates',
        'OpenAI o1 reasoning model',
        'Multi-select & SmartMatch integration',
        'Realistic contact enrichment'
      ],
      badge: 'Best',
      badgeColor: 'bg-violet-500'
    },
    {
      icon: Sparkles,
      title: 'SmartMatch',
      description: 'AI-powered candidate scoring with instant job fit analysis',
      gradient: 'from-purple-500 to-pink-500',
      hoverGradient: 'group-hover:from-purple-600 group-hover:to-pink-600',
      route: '/smartmatch',
      features: [
        'Score up to 5 candidates (0-10)',
        'Technical & cultural fit analysis',
        'Resume & LinkedIn support',
        'Instant rankings & recommendations'
      ],
      badge: 'AI-Powered',
      badgeColor: 'bg-purple-500'
    },
    {
      icon: Brain,
      title: 'Sourcing Doctor',
      description: 'AI sourcing expert that diagnoses challenges and prescribes solutions',
      gradient: 'from-orange-500 to-red-500',
      hoverGradient: 'group-hover:from-orange-600 group-hover:to-red-600',
      route: '/ojas',
      features: [
        'AI-powered sourcing advice',
        'Boolean search generation',
        'Channel recommendations',
        'Real-time solutions'
      ],
      badge: 'Expert',
      badgeColor: 'bg-orange-500'
    },
    {
      icon: Trophy,
      title: 'TA Certifications',
      description: 'Industry-recognized certifications to advance your recruiting career',
      gradient: 'from-green-500 to-emerald-500',
      hoverGradient: 'group-hover:from-green-600 group-hover:to-emerald-600',
      route: '/certifications',
      features: [
        'Bronze, Silver & Gold levels',
        'Industry-recognized badges',
        'Comprehensive curriculum',
        'LinkedIn shareable certificates'
      ],
      badge: 'Career',
      badgeColor: 'bg-green-500'
    }
    /* HIDDEN: Awards feature temporarily disabled
    ,{
      icon: Award,
      title: 'TA Awards',
      description: 'Recognize and celebrate excellence in talent acquisition globally',
      gradient: 'from-yellow-500 to-amber-500',
      hoverGradient: 'group-hover:from-yellow-600 group-hover:to-amber-600',
      route: '/awards',
      features: [
        'Global recognition program',
        'Multiple award categories',
        'Nominations open November 1st',
        'Celebrate recruiting excellence'
      ],
      badge: '2025',
      badgeColor: 'bg-yellow-500'
    }
    */
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 relative overflow-hidden">
      <SEO 
        title={seoConfig.home.title}
        description={seoConfig.home.description}
        canonical={seoConfig.home.canonical}
        keywords={seoConfig.home.keywords}
        schema={softwareApplicationSchema}
      />
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse delay-700"></div>
        <div className="absolute top-1/2 right-1/3 w-64 h-64 bg-cyan-500/10 rounded-full blur-2xl animate-pulse delay-1000"></div>
      </div>

      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || mobileMenuOpen ? 'bg-slate-900/95 backdrop-blur-lg border-b border-slate-800 shadow-lg' : 'bg-transparent'
      }`}>
        <AwardsBanner />
        <ProfileNudgeBanner />
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
              <img src="/images/triq-logo.png" alt="TRIQ - The Recruiter IQ" className="w-10 h-10 rounded-full object-contain" />
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                  RecruiterIQ
                </h1>
                <p className="text-xs text-slate-400 hidden sm:block">AI-Powered Recruiting Platform</p>
              </div>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-6">
              {/* HIDDEN: Awards temporarily disabled
              <button
                onClick={() => navigate('/awards')}
                className="text-slate-300 hover:text-white transition-colors text-sm font-medium"
              >
                Awards
              </button>
              */}
              <button
                data-testid="nav-sourcing-studio"
                onClick={() => navigate('/sourcing-studio')}
                className="text-slate-300 hover:text-white transition-colors text-sm font-medium flex items-center gap-1"
              >
                Sourcing Studio
                <span className="text-xs bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-2 py-0.5 rounded font-semibold">NEW</span>
              </button>
              <button
                data-testid="nav-latest-news"
                onClick={() => navigate('/news')}
                className="text-slate-300 hover:text-white transition-colors text-sm font-medium"
              >
                Latest News
              </button>
              <button
                data-testid="nav-latest-trends"
                onClick={() => navigate('/trends')}
                className="text-slate-300 hover:text-white transition-colors text-sm font-medium"
              >
                Latest Trends
              </button>
              <button
                data-testid="nav-ta-jobs"
                onClick={() => navigate('/ta-global-jobs')}
                className="text-slate-300 hover:text-white transition-colors text-sm font-medium"
              >
                TA Jobs
              </button>
              <button
                data-testid="nav-ta-library"
                onClick={() => navigate('/library')}
                className="text-slate-300 hover:text-white transition-colors text-sm font-medium"
              >
                TA Library
              </button>
              <button
                data-testid="nav-community"
                onClick={() => navigate('/community')}
                className="text-slate-300 hover:text-white transition-colors text-sm font-medium"
              >
                Community
              </button>
              <button
                data-testid="nav-events"
                onClick={() => navigate('/events')}
                className="text-slate-300 hover:text-white transition-colors text-sm font-medium"
              >
                Events
              </button>
              <button
                data-testid="nav-for-employers"
                onClick={() => navigate('/employers')}
                className="text-slate-300 hover:text-white transition-colors text-sm font-medium"
              >
                For Employers
              </button>
              <button
                data-testid="nav-blog"
                onClick={() => navigate('/blog')}
                className="text-slate-300 hover:text-white transition-colors text-sm font-medium flex items-center gap-1"
              >
                Blog
                <span className="text-xs bg-gradient-to-r from-purple-500 to-indigo-500 text-white px-2 py-0.5 rounded font-semibold">NEW</span>
              </button>
              
              {isAuthenticated && user ? (
                <div className="relative">
                  <button
                    data-testid="user-menu-trigger"
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center gap-3 px-4 py-2 rounded-lg bg-slate-800/50 hover:bg-slate-800 border border-slate-700 transition-all"
                  >
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white font-semibold text-sm">
                      {user.name?.charAt(0).toUpperCase() || 'U'}
                    </div>
                    <span className="text-sm font-medium text-white">{user.name}</span>
                  </button>
                  
                  {showUserMenu && (
                    <div className="absolute right-0 top-full mt-2 w-64 bg-slate-800 border border-slate-700 rounded-xl shadow-2xl overflow-hidden z-[10001]">
                      <div className="p-4 border-b border-slate-700 bg-gradient-to-br from-blue-900/20 to-slate-800">
                        <p className="text-sm font-semibold text-white">{user.name}</p>
                        <p className="text-xs text-slate-400 mt-1">{user.email}</p>
                        
                        {/* Share Profile Button */}
                        <button
                          onClick={() => {
                            const profileLink = `${window.location.origin}/profile/${user.user_id}/public`;
                            navigator.clipboard.writeText(profileLink).catch(() => {});
                            toast({
                              title: 'Profile Link Copied! ✓',
                              description: 'Share your public profile with anyone',
                            });
                          }}
                          className="w-full mt-3 px-3 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white rounded-lg text-xs font-medium flex items-center justify-center gap-2 transition-all"
                        >
                          <Share2 className="w-3.5 h-3.5" />
                          Share My Profile
                        </button>
                      </div>
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          navigate('/my-certifications');
                        }}
                        className="w-full text-left px-4 py-3 text-sm text-slate-300 hover:bg-slate-700/50 flex items-center gap-2 transition-colors"
                      >
                        <Award className="w-4 h-4" />
                        My Certifications
                      </button>
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          navigate('/my-activity');
                        }}
                        className="w-full text-left px-4 py-3 text-sm text-slate-300 hover:bg-slate-700/50 flex items-center gap-2 transition-colors"
                      >
                        <BarChart3 className="w-4 h-4" />
                        My Activity
                      </button>
                      <button
                        data-testid="menu-360-profile"
                        onClick={() => {
                          setShowUserMenu(false);
                          navigate('/profile');
                        }}
                        className="w-full text-left px-4 py-3 text-sm text-slate-300 hover:bg-slate-700/50 flex items-center gap-2 transition-colors"
                      >
                        <Users className="w-4 h-4" />
                        My 360° Profile
                      </button>
                      <button
                        data-testid="menu-matched-jobs"
                        onClick={() => {
                          setShowUserMenu(false);
                          navigate('/matched-jobs');
                        }}
                        className="w-full text-left px-4 py-3 text-sm text-slate-300 hover:bg-slate-700/50 flex items-center gap-2 transition-colors"
                      >
                        <Briefcase className="w-4 h-4" />
                        Matched Jobs
                      </button>
                      <button
                        data-testid="menu-messages"
                        onClick={() => {
                          setShowUserMenu(false);
                          navigate('/messages');
                        }}
                        className="w-full text-left px-4 py-3 text-sm text-slate-300 hover:bg-slate-700/50 flex items-center gap-2 transition-colors"
                      >
                        <BarChart3 className="w-4 h-4" />
                        Messages
                      </button>
                      <button
                        data-testid="menu-referrals"
                        onClick={() => {
                          setShowUserMenu(false);
                          navigate('/referrals');
                        }}
                        className="w-full text-left px-4 py-3 text-sm text-slate-300 hover:bg-slate-700/50 flex items-center gap-2 transition-colors"
                      >
                        <Award className="w-4 h-4" />
                        Refer & Earn Membership
                      </button>
                      <button
                        data-testid="menu-extension"
                        onClick={() => {
                          setShowUserMenu(false);
                          navigate('/extension');
                        }}
                        className="w-full text-left px-4 py-3 text-sm text-slate-300 hover:bg-slate-700/50 flex items-center gap-2 transition-colors"
                      >
                        <Zap className="w-4 h-4" />
                        Get Browser Extension
                        <span className="text-[10px] bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-1.5 py-0.5 rounded font-bold">NEW</span>
                      </button>
                      <button
                        data-testid="menu-job-projects"
                        onClick={() => {
                          setShowUserMenu(false);
                          navigate('/job-projects');
                        }}
                        className="w-full text-left px-4 py-3 text-sm text-slate-300 hover:bg-slate-700/50 flex items-center gap-2 transition-colors"
                      >
                        <Briefcase className="w-4 h-4" />
                        My Job Projects
                      </button>
                      {/* HIDDEN: Awards Progress temporarily disabled
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          navigate('/awards-progress');
                        }}
                        className="w-full text-left px-4 py-3 text-sm text-slate-300 hover:bg-slate-700/50 flex items-center gap-2 transition-colors"
                      >
                        <Trophy className="w-4 h-4" />
                        Awards Progress
                      </button>
                      */}
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          navigate('/profile');
                        }}
                        className="w-full text-left px-4 py-3 text-sm text-slate-300 hover:bg-slate-700/50 flex items-center gap-2 transition-colors"
                      >
                        <User className="w-4 h-4" />
                        My Profile
                      </button>
                      <div className="border-t border-slate-700"></div>
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          logout();
                          toast({
                            title: 'Logged Out',
                            description: 'You have been successfully logged out.',
                          });
                        }}
                        className="w-full text-left px-4 py-3 text-sm text-red-400 hover:bg-red-900/20 flex items-center gap-2 transition-colors"
                      >
                        <LogOut className="w-4 h-4" />
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Button
                  onClick={() => navigate('/login')}
                  className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white px-6 shadow-lg shadow-blue-500/30"
                >
                  Login
                </Button>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-slate-300 hover:text-white"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="lg:hidden mt-4 pb-4 border-t border-slate-800 pt-4 animate-fadeIn max-h-[calc(100vh-120px)] overflow-y-auto">
              <div className="flex flex-col gap-2">
                {/* HIDDEN: Awards temporarily disabled
                <button
                  onClick={() => {
                    navigate('/awards');
                    setMobileMenuOpen(false);
                  }}
                  className="text-left px-4 py-2 text-slate-300 hover:bg-slate-800 rounded-lg transition-colors"
                >
                  Awards
                </button>
                */}
                <button
                  data-testid="mobile-nav-sourcing-studio"
                  onClick={() => {
                    navigate('/sourcing-studio');
                    setMobileMenuOpen(false);
                  }}
                  className="text-left px-4 py-2 text-slate-300 hover:bg-slate-800 rounded-lg transition-colors"
                >
                  Sourcing Studio
                </button>
                <button
                  data-testid="mobile-nav-latest-news"
                  onClick={() => {
                    navigate('/news');
                    setMobileMenuOpen(false);
                  }}
                  className="text-left px-4 py-2 text-slate-300 hover:bg-slate-800 rounded-lg transition-colors"
                >
                  Latest News
                </button>
                <button
                  data-testid="mobile-nav-latest-trends"
                  onClick={() => {
                    navigate('/trends');
                    setMobileMenuOpen(false);
                  }}
                  className="text-left px-4 py-2 text-slate-300 hover:bg-slate-800 rounded-lg transition-colors"
                >
                  Latest Trends
                </button>
                <button
                  data-testid="mobile-nav-ta-jobs"
                  onClick={() => {
                    navigate('/ta-global-jobs');
                    setMobileMenuOpen(false);
                  }}
                  className="text-left px-4 py-2 text-slate-300 hover:bg-slate-800 rounded-lg transition-colors"
                >
                  TA Jobs
                </button>
                <button
                  data-testid="mobile-nav-ta-library"
                  onClick={() => {
                    navigate('/library');
                    setMobileMenuOpen(false);
                  }}
                  className="text-left px-4 py-2 text-slate-300 hover:bg-slate-800 rounded-lg transition-colors"
                >
                  TA Library
                </button>
                <button
                  data-testid="mobile-nav-blog"
                  onClick={() => {
                    navigate('/blog');
                    setMobileMenuOpen(false);
                  }}
                  className="text-left px-4 py-2 text-slate-300 hover:bg-slate-800 rounded-lg transition-colors flex items-center gap-2"
                >
                  Blog
                  <span className="text-xs bg-gradient-to-r from-purple-500 to-indigo-500 text-white px-2 py-0.5 rounded font-semibold">NEW</span>
                </button>
                <button
                  data-testid="mobile-nav-community"
                  onClick={() => {
                    navigate('/community');
                    setMobileMenuOpen(false);
                  }}
                  className="text-left px-4 py-2 text-slate-300 hover:bg-slate-800 rounded-lg transition-colors"
                >
                  Community
                </button>
                <button
                  data-testid="mobile-nav-events"
                  onClick={() => {
                    navigate('/events');
                    setMobileMenuOpen(false);
                  }}
                  className="text-left px-4 py-2 text-slate-300 hover:bg-slate-800 rounded-lg transition-colors"
                >
                  Events
                </button>
                <button
                  data-testid="mobile-nav-for-employers"
                  onClick={() => {
                    navigate('/employers');
                    setMobileMenuOpen(false);
                  }}
                  className="text-left px-4 py-2 text-slate-300 hover:bg-slate-800 rounded-lg transition-colors"
                >
                  For Employers
                </button>
                {isAuthenticated ? (
                  <>
                    <button
                      onClick={() => {
                        navigate('/profile');
                        setMobileMenuOpen(false);
                      }}
                      className="text-left px-4 py-2 text-slate-300 hover:bg-slate-800 rounded-lg transition-colors"
                    >
                      My 360° Profile
                    </button>
                    <button
                      onClick={() => {
                        navigate('/matched-jobs');
                        setMobileMenuOpen(false);
                      }}
                      className="text-left px-4 py-2 text-slate-300 hover:bg-slate-800 rounded-lg transition-colors"
                    >
                      Matched Jobs
                    </button>
                    <button
                      onClick={() => {
                        navigate('/messages');
                        setMobileMenuOpen(false);
                      }}
                      className="text-left px-4 py-2 text-slate-300 hover:bg-slate-800 rounded-lg transition-colors"
                    >
                      Messages
                    </button>
                    <button
                      onClick={() => {
                        navigate('/my-activity');
                        setMobileMenuOpen(false);
                      }}
                      className="text-left px-4 py-2 text-slate-300 hover:bg-slate-800 rounded-lg transition-colors"
                    >
                      My Activity
                    </button>
                    {/* HIDDEN: Awards Progress temporarily disabled
                    <button
                      onClick={() => {
                        navigate('/awards-progress');
                        setMobileMenuOpen(false);
                      }}
                      className="text-left px-4 py-2 text-slate-300 hover:bg-slate-800 rounded-lg transition-colors"
                    >
                      Awards Progress
                    </button>
                    */}
                    <button
                      onClick={() => {
                        navigate('/profile');
                        setMobileMenuOpen(false);
                      }}
                      className="text-left px-4 py-2 text-slate-300 hover:bg-slate-800 rounded-lg transition-colors"
                    >
                      My Profile
                    </button>
                    <button
                      onClick={() => {
                        logout();
                        setMobileMenuOpen(false);
                      }}
                      className="text-left px-4 py-2 text-red-400 hover:bg-red-900/20 rounded-lg transition-colors"
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => {
                      navigate('/login');
                      setMobileMenuOpen(false);
                    }}
                    className="text-left px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors"
                  >
                    Login
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16 animate-fadeIn">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-full mb-8 backdrop-blur-sm">
              <Zap className="w-4 h-4 text-blue-400" />
              <span className="text-sm text-blue-300 font-medium">AI-Powered Recruiting Platform</span>
            </div>
            
            {/* Main Headline */}
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              <span className="bg-gradient-to-r from-white via-blue-100 to-cyan-200 bg-clip-text text-transparent">
                Transform Your
              </span>
              <br />
              <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-purple-400 bg-clip-text text-transparent">
                Recruiting Game
              </span>
            </h1>
            
            {/* Subheadline */}
            <p className="text-xl md:text-2xl text-slate-300 mb-12 max-w-3xl mx-auto leading-relaxed">
              From market intelligence to candidate matching—everything you need to make 
              <span className="text-cyan-400 font-semibold"> smarter, faster </span>
              hiring decisions with AI.
            </p>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                onClick={() => setShowGetStartedModal(true)}
                className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white px-8 py-6 text-lg shadow-2xl shadow-blue-500/30 group"
              >
                Get Started Free
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                data-testid="hero-sourcing-studio-cta"
                onClick={() => navigate('/sourcing-studio')}
                className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white px-8 py-6 text-lg shadow-2xl shadow-purple-500/30 group"
              >
                Enter the Sourcing Studio
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                onClick={() => navigate('/certifications')}
                variant="outline"
                className="border-2 border-slate-700 bg-slate-800/50 hover:bg-slate-800 text-white px-8 py-6 text-lg backdrop-blur-sm"
              >
                Explore Certifications
              </Button>
            </div>

            {/* Social Proof */}
            <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-8 text-slate-400">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span>No credit card required</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span>Instant access</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span>AI-powered insights</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="relative px-6 pb-20">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">
              Everything You Need to
              <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent"> Win Talent</span>
            </h2>
            <p className="text-xl text-slate-400">
              Powerful AI tools designed for modern recruiters
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card
                  key={index}
                  onClick={() => navigate(feature.route)}
                  className="group relative bg-slate-900/50 border-2 border-slate-800 hover:border-slate-700 backdrop-blur-sm cursor-pointer transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl overflow-hidden"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  {/* Gradient Overlay */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}></div>
                  
                  <CardHeader className="relative">
                    {/* Badge */}
                    <div className="absolute top-0 right-0">
                      <Badge className={`${feature.badgeColor} text-white text-xs`}>
                        {feature.badge}
                      </Badge>
                    </div>
                    
                    {/* Icon */}
                    <div className={`w-14 h-14 bg-gradient-to-br ${feature.gradient} rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg ${feature.hoverGradient}`}>
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    
                    <CardTitle className="text-2xl text-white mb-2 group-hover:text-cyan-400 transition-colors">
                      {feature.title}
                    </CardTitle>
                    <CardDescription className="text-slate-400 text-base">
                      {feature.description}
                    </CardDescription>
                  </CardHeader>
                  
                  <CardContent className="relative">
                    <ul className="space-y-2.5 mb-6">
                      {feature.features.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2.5 text-sm text-slate-300">
                          <CheckCircle className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <Button
                      className="w-full bg-slate-800 hover:bg-slate-700 text-white group-hover:bg-gradient-to-r group-hover:from-blue-600 group-hover:to-cyan-600 transition-all"
                    >
                      Explore
                      <ChevronRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* TA Awards Ribbon - Nominations Open November 1st */}
          {/* HIDDEN: Awards banner temporarily disabled
          <div className="mt-12 relative">
            <div className="bg-gradient-to-r from-yellow-500 via-amber-500 to-yellow-500 rounded-2xl p-8 relative overflow-hidden shadow-2xl">
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-white via-transparent to-transparent"></div>
              </div>
              
              <div className="relative z-10 flex items-center justify-between flex-wrap gap-6">
                <div className="flex items-center gap-6">
                  <div className="w-20 h-20 bg-white rounded-2xl flex items-center justify-center shadow-xl">
                    <Award className="w-12 h-12 text-yellow-600" />
                  </div>
                  <div>
                    <h3 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
                      TA Awards 2025
                      <Badge className="bg-white text-yellow-600 text-sm px-3 py-1">
                        Coming Soon
                      </Badge>
                    </h3>
                    <p className="text-yellow-100 text-lg font-medium">
                      🎉 Nominations Open November 1st, 2025
                    </p>
                    <p className="text-yellow-50 text-sm mt-1">
                      Celebrate excellence in talent acquisition • Global recognition • Multiple categories
                    </p>
                  </div>
                </div>
                
                <Button
                  onClick={() => navigate('/awards')}
                  className="bg-white text-yellow-600 hover:bg-yellow-50 font-bold px-8 py-6 text-lg shadow-xl hover:shadow-2xl transition-all hover:scale-105"
                >
                  Learn More About Awards
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
              
              <div className="absolute top-4 right-4 text-yellow-300 opacity-20 animate-pulse">
                <Trophy className="w-16 h-16" />
              </div>
              <div className="absolute bottom-4 left-1/3 text-yellow-300 opacity-20 animate-pulse delay-500">
                <Award className="w-12 h-12" />
              </div>
            </div>
          </div>
          */}
        </div>
      </section>

      {/* Stats Section */}
      <section className="relative px-6 py-20 border-y border-slate-800">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { label: 'TA Jobs', value: '1M+', icon: Briefcase },
              { label: 'AI Models', value: '6+', icon: Brain },
              { label: 'Happy Users', value: '10K+', icon: Users },
              { label: 'Certifications', value: '3', icon: Trophy }
            ].map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div key={index} className="text-center">
                  <Icon className="w-8 h-8 text-cyan-400 mx-auto mb-3" />
                  <div className="text-4xl font-bold text-white mb-2">{stat.value}</div>
                  <div className="text-slate-400">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />

      {/* Get Started Modal */}
      <GetStartedModal
        isOpen={showGetStartedModal}
        onClose={() => setShowGetStartedModal(false)}
      />

      {/* Engagement Modal */}
      {showEngagementModal && (
        <EngagementModal onClose={() => setShowEngagementModal(false)} />
      )}

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.8s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default HomePage;
